import React, { useState } from 'react';
import type { MenuProps } from '@mui/material/Menu';
import MuiMenu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';

import { Box } from '@mui/material';

import { colours } from 'src/constants/colours';

import useLoginModal from 'src/hooks/useLoginModal';
import { styledComponent } from 'src/utils/styled';

const StyledMenu = styledComponent(MuiMenu)`
    .MuiList-root {
        padding: 0;
    }
    .MuiPaper-root {
        box-shadow: 0 0.625rem 1.25rem ${colours.blackOpacity.O08};
    }
`;

StyledMenu.defaultProps = {};

export const MenuItem = styledComponent(MuiMenuItem)`
    :hover {
        background-color: ${colours.secondary.O08};
    }
    :not(:first-of-type) {
        border-top: 0.0625rem solid ${colours.blackOpacity.O08};
    }
`;
interface Props extends Omit<MenuProps, 'open' | 'anchorEl' | 'onClose'> {
    items: Array<React.ReactElement | false | undefined | null>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    children: React.ReactElement<any, string | React.JSXElementConstructor<any>>; // children is the component we click on to open the menu
    extraOpenAction?: () => void;
    extraCloseAction?: () => void;
    authenticatedBeforeOpen?: boolean;
    disabled?: boolean;
}
const Menu: React.FC<Props> = ({
    items,
    extraOpenAction,
    extraCloseAction,
    authenticatedBeforeOpen,
    disabled,
    ...props
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const { createAuthenticatedHandler } = useLoginModal();
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setAnchorEl(event.currentTarget);
        extraOpenAction?.();
    };
    const handleClose = () => {
        setAnchorEl(null);
        extraCloseAction?.();
    };

    // If there are no items in the menu, don't show the menu at all!
    if (!items.some(Boolean)) {
        return null;
    }

    const handleClick = async (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!disabled) {
            if (authenticatedBeforeOpen) {
                await createAuthenticatedHandler(() => handleOpen(ev));
            } else {
                handleOpen(ev);
            }
        }
    };

    return (
        <>
            <Box
                display="flex"
                onClick={handleClick}
                aria-controls={!props['aria-controls'] && open ? 'basic-menu' : props['aria-controls']}
                aria-expanded={!props['aria-expanded'] && open ? 'true' : props['aria-expanded']}
            >
                {props.children}
            </Box>
            <StyledMenu
                {...props}
                anchorEl={anchorEl}
                open={disabled ? false : Boolean(anchorEl)}
                onClick={handleClose}
            >
                {items}
            </StyledMenu>
        </>
    );
};
export default Menu;
